import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { FileMetadata, FolderStructure } from '../../../shared/types';
import { apiService } from '../services/api';
import { DeletionConfirmationModal } from './DeletionConfirmationModal';
import { handleApiError } from '../utils/api';

interface FileDeletionProps {
  selectedFiles: FileMetadata[];
  selectedFolders: FolderStructure[];
  onDeletionComplete?: () => void;
  onDeletionError?: (error: string) => void;
  className?: string;
}

export const FileDeletion: React.FC<FileDeletionProps> = ({
  selectedFiles,
  selectedFolders,
  onDeletionComplete,
  onDeletionError,
  className = ''
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const queryClient = useQueryClient();

  // Mutation for deleting files
  const deleteFilesMutation = useMutation({
    mutationFn: async (fileIds: string[]) => {
      const results = await Promise.allSettled(
        fileIds.map(fileId => apiService.deleteFile(fileId))
      );
      
      const failures = results
        .map((result, index) => ({ result, fileId: fileIds[index] }))
        .filter(({ result }) => result.status === 'rejected')
        .map(({ result, fileId }) => ({
          fileId,
          error: result.status === 'rejected' ? result.reason : 'Unknown error'
        }));

      if (failures.length > 0) {
        throw new Error(`Failed to delete ${failures.length} file(s): ${failures.map(f => f.error).join(', ')}`);
      }

      return results;
    },
    onSuccess: () => {
      // Invalidate and refetch file and folder lists
      queryClient.invalidateQueries({ queryKey: ['files'] });
      queryClient.invalidateQueries({ queryKey: ['folders'] });
      onDeletionComplete?.();
    },
    onError: (error) => {
      const errorMessage = handleApiError(error);
      onDeletionError?.(errorMessage);
    }
  });

  // Mutation for deleting folders
  const deleteFoldersMutation = useMutation({
    mutationFn: async (folderPaths: string[]) => {
      const results = await Promise.allSettled(
        folderPaths.map(folderPath => {
          // Extract folder ID from path - this might need adjustment based on API design
          const folderId = encodeURIComponent(folderPath);
          return apiService.deleteFolder(folderId);
        })
      );
      
      const failures = results
        .map((result, index) => ({ result, folderPath: folderPaths[index] }))
        .filter(({ result }) => result.status === 'rejected')
        .map(({ result, folderPath }) => ({
          folderPath,
          error: result.status === 'rejected' ? result.reason : 'Unknown error'
        }));

      if (failures.length > 0) {
        throw new Error(`Failed to delete ${failures.length} folder(s): ${failures.map(f => f.error).join(', ')}`);
      }

      return results;
    },
    onSuccess: () => {
      // Invalidate and refetch file and folder lists
      queryClient.invalidateQueries({ queryKey: ['files'] });
      queryClient.invalidateQueries({ queryKey: ['folders'] });
      onDeletionComplete?.();
    },
    onError: (error) => {
      const errorMessage = handleApiError(error);
      onDeletionError?.(errorMessage);
    }
  });

  // Combined deletion mutation
  const combinedDeletionMutation = useMutation({
    mutationFn: async () => {
      const fileIds = selectedFiles.map(file => file.fileId);
      const folderPaths = selectedFolders.map(folder => folder.folderPath);

      // Delete files first, then folders
      const promises: Promise<any>[] = [];
      
      if (fileIds.length > 0) {
        promises.push(deleteFilesMutation.mutateAsync(fileIds));
      }
      
      if (folderPaths.length > 0) {
        promises.push(deleteFoldersMutation.mutateAsync(folderPaths));
      }

      await Promise.all(promises);
    },
    onSuccess: () => {
      setIsModalOpen(false);
      onDeletionComplete?.();
    },
    onError: (error) => {
      const errorMessage = handleApiError(error);
      onDeletionError?.(errorMessage);
      setIsModalOpen(false);
    }
  });

  const handleDeleteClick = () => {
    setIsModalOpen(true);
  };

  const handleConfirmDeletion = async () => {
    await combinedDeletionMutation.mutateAsync();
  };

  const handleCloseModal = () => {
    if (!combinedDeletionMutation.isPending) {
      setIsModalOpen(false);
    }
  };

  const hasSelection = selectedFiles.length > 0 || selectedFolders.length > 0;
  const isDeleting = combinedDeletionMutation.isPending;

  if (!hasSelection) {
    return null;
  }

  return (
    <>
      <button
        onClick={handleDeleteClick}
        disabled={isDeleting}
        className={`inline-flex items-center px-3 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      >
        <Trash2 className="w-4 h-4 mr-2" />
        {isDeleting ? 'Deleting...' : 'Delete'}
      </button>

      <DeletionConfirmationModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDeletion}
        selectedFiles={selectedFiles}
        selectedFolders={selectedFolders}
        isDeleting={isDeleting}
      />
    </>
  );
};